import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Lab investigation v2 API client (api-lab-investigation-v2-service).
 *
 * Contract notes:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 2. `test` is a snapshot from the catalog pick: `{sltCode, ltCode, name,
 *    panel, unit, refRange}`. Free-text tests are allowed too (sltCode +
 *    ltCode empty). An optional `tag` qualifies readings that share one test
 *    ("Morning systolic", "PP2"); catalogue picks have none. Add, batch and
 *    update REPLACE the stored snapshot, tag included — send the stored tag
 *    back or it is cleared.
 *
 * 3. `numericValue` (float) is optional; server computes the `flag` from it
 *    plus `test.refRange.low/high`. Missing numeric or range ⇒ `flag = unknown`.
 *
 * 4. Batch endpoint appends N investigations under one version bump — used
 *    by the digitisation flow after the user reviews the parsed rows.
 *
 * 5. Delete is soft.
 */
class LabInvestigationService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.labInvestigationV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Batch add — the sole append path (single-Add endpoint retired per
   * ADR-0001 Phase 3). Section-form single-row appends wrap their body
   * in a length-1 `investigations` array; the digitisation review step
   * sends the full batch. Server dedups by testIdentityKey.
   *   { documentId?, source, investigations: [{test, testDate, value, ...}],
   *     expectedVersion, sourceTranscriptId? }
   */
  batch(patientId, body, onResponse, onError) {
    const url = APIConfig.labInvestigationV2.batch.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  update(patientId, investigationId, body, onResponse, onError) {
    const url = APIConfig.labInvestigationV2.update.format(patientId, investigationId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Mark an audit-history entry as incorrect. `index` = zero-based index
   * into the target investigation's `statusHistory[]`. Server refuses if
   * the row is already marked or already corrected (mutually exclusive).
   * Body: { expectedVersion }.
   */
  markIncorrect(patientId, investigationId, index, expectedVersion, onResponse, onError) {
    const url = APIConfig.labInvestigationV2.markIncorrect.format(
      patientId, investigationId, index,
    );
    return this.apiService.post(url, { expectedVersion: expectedVersion || 0 }, onResponse, onError);
  }

  /**
   * Correct one audit-history entry. `body` shape:
   *   { testDate, value, numericValue?, unit, labName?, description?, expectedVersion }
   * Server snapshots the pre-correction values (for the "Correction history"
   * popup) and recomputes Flag from value + refRange. Refuses if the row
   * is marked incorrect or already corrected.
   */
  correctHistory(patientId, investigationId, index, body, onResponse, onError) {
    const url = APIConfig.labInvestigationV2.correctHistory.format(
      patientId, investigationId, index,
    );
    return this.apiService.post(url, body, onResponse, onError);
  }

  delete(patientId, investigationId, expectedVersion, onResponse, onError) {
    const url = APIConfig.labInvestigationV2.delete.format(patientId, investigationId);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default LabInvestigationService;
